// ============================================================
// GitSecurity.jsx
//
// A branded, responsive course content page for the
// "Git Security & GPG Signing" lesson.
//
// Author: ProDev Enterprise Series (DevOps Branch)
// Features: GPG signature verification, credential management,
//           and secret prevention strategies.
// ============================================================

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Paper,
    Fab,
    Zoom,
    Stack,
    Button
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import SecurityIcon           from "@mui/icons-material/Security";
import VerifiedUserIcon       from "@mui/icons-material/VerifiedUser";
import VpnKeyIcon             from "@mui/icons-material/VpnKey";
import ShieldIcon             from "@mui/icons-material/Shield";
import TerminalIcon           from "@mui/icons-material/Terminal";
import LightbulbIcon          from "@mui/icons-material/Lightbulb";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon        from "@mui/icons-material/ContentCopy";
import DoneIcon               from "@mui/icons-material/Done";
import SchoolIcon             from "@mui/icons-material/School";
import StarIcon               from "@mui/icons-material/Star";
import KeyboardArrowUpIcon    from "@mui/icons-material/KeyboardArrowUp";
import ArrowBackIcon          from "@mui/icons-material/ArrowBack";
import LockIcon               from "@mui/icons-material/Lock";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg:          "#233D4D",
    surface:     "rgba(255,255,255,0.04)",
    border:      "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",
    goldBright:  "#FFD700",
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Sub-Components ────────────────────────────────────────

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: "50%",
                background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

function CodeBlock({ title, code }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(code).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 4 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BRAND.border}`, background: "rgba(0,0,0,0.3)" }}>
                <Typography sx={{ color: BRAND.gold, fontSize: 11, fontWeight: 700, fontFamily: "monospace", textTransform: 'uppercase' }}>{title}</Typography>
                <Tooltip title={copied ? "Copied!" : "Copy code"} placement="top">
                    <Box onClick={handleCopy} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, color: copied ? BRAND.goldBright : BRAND.muted, fontSize: 12 }}>
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2.5 }}>
                <Typography component="pre" sx={{ fontFamily: "monospace", fontSize: 13, color: BRAND.codeText, whiteSpace: "pre-wrap", m: 0 }}>
                    {code}
                </Typography>
            </Box>
        </Box>
    );
}

// ── Main Component ────────────────────────────────────────

export default function GitSecurity() {
    const [showScroll, setShowScroll] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            if (window.pageYOffset > 400) setShowScroll(true);
            else setShowScroll(false);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 },
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`
        }}>
            <Container maxWidth="lg">
                {/* HERO HEADER */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip icon={<SecurityIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />} label="DevOps · Identity & Protection"
                          sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11 }} />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 28, sm: 36, md: 44 }, mb: 2 }}>
                        Git Security & <span>GPG Signing</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 620, lineHeight: 1.75, mx: { xs: "auto", md: 0 } }}>
                        Trust is the foundation of collaboration. Learn how to verify your identity with GPG signatures, manage credentials without plaintext risks, and prevent sensitive secrets from ever leaking into your history.
                    </Typography>
                </Box>

                {/* 1. GPG SIGNING */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<VerifiedUserIcon />} label="1. Digital Identity (GPG)" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        GPG (GNU Privacy Guard) allows you to cryptographically sign your commits. This proves that you—and only you—authored the changes, earning a "Verified" badge on GitHub.
                    </Typography>
                    <CodeBlock
                        title="Key Generation & Config"
                        code={`# 1. Generate a new GPG key\ngpg --full-generate-key\n\n# 2. Get your Long Key ID\ngpg --list-secret-keys --keyid-format=long\n\n# 3. Inform Git of your identity\ngit config --global user.signingkey <YOUR_KEY_ID>\ngit config --global commit.gpgsign true`}
                    />
                </Box>

                {/* 2. CREDENTIAL HELPERS */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<VpnKeyIcon />} label="2. Secure Credential Storage" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        Avoid typing passwords repeatedly. Use credential helpers to store your Personal Access Tokens (PATs) securely in your OS keychain.
                    </Typography>
                    <Grid container spacing={2}>
                        {[
                            { os: "Windows", cmd: "git config --global credential.helper manager-core" },
                            { os: "macOS", cmd: "git config --global credential.helper osxkeychain" },
                            { os: "Linux (Cache)", cmd: "git config --global credential.helper 'cache --timeout=3600'" }
                        ].map((helper, i) => (
                            <Grid item xs={12} md={4} key={i}>
                                <Paper sx={{ p: 2, background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 2 }}>
                                    <Typography sx={{ color: BRAND.gold, fontWeight: 700, fontSize: 13, mb: 0.5 }}>{helper.os}</Typography>
                                    <Typography sx={{ color: BRAND.white, fontFamily: 'monospace', fontSize: 10 }}>{helper.cmd}</Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* 3. SECRET PREVENTION */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<ShieldIcon />} label="3. Preventing Secret Leaks" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        Accidentally pushing an API key is a critical security breach. Use a multi-layered defense to keep secrets out of your repository.
                    </Typography>
                    <Paper sx={{ p: 3, background: "rgba(255, 0, 0, 0.05)", border: `1px solid rgba(255, 0, 0, 0.2)`, borderRadius: 2, mb: 3 }}>
                        <Stack spacing={1.5}>
                            <Box sx={{ display: 'flex', gap: 1.5 }}><LockIcon sx={{ color: "#ff4444", fontSize: 20 }} /><Typography sx={{ color: BRAND.white, fontSize: 13 }}>Always list <strong>.env</strong> and <strong>config.json</strong> in your .gitignore.</Typography></Box>
                            <Box sx={{ display: 'flex', gap: 1.5 }}><LockIcon sx={{ color: "#ff4444", fontSize: 20 }} /><Typography sx={{ color: BRAND.white, fontSize: 13 }}>Use <strong>gitleaks</strong> or <strong>trufflehog</strong> to scan your history for existing secrets.</Typography></Box>
                            <Box sx={{ display: 'flex', gap: 1.5 }}><LockIcon sx={{ color: "#ff4444", fontSize: 20 }} /><Typography sx={{ color: BRAND.white, fontSize: 13 }}>If a secret is pushed: Revoke it immediately, rotate it, and purge history.</Typography></Box>
                        </Stack>
                    </Paper>
                    <CodeBlock
                        title="The Safety Workflow"
                        code={`# 1. Create a secret file\necho "AWS_KEY=secret" >> .env\n\n# 2. Immediately ignore it\necho ".env" >> .gitignore\n\n# 3. Safely commit the ignore file\ngit add .gitignore\ngit commit -m "security: ignore env files"`}
                    />
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* BEST PRACTICES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="Security Checklist" />
                    <Grid container spacing={1.5}>
                        {[
                            "Use Personal Access Tokens (PATs) or SSH keys instead of account passwords.",
                            "Enable Two-Factor Authentication (2FA) on your GitHub account.",
                            "Sign every commit to build a chain of trust in your repository history.",
                            "Run secret scanners as part of your pre-commit hooks or CI/CD pipelines.",
                            "Periodically audit your 'git config --list' for unauthorized entries.",
                            "Treat commit history as public property; assume everything will be read."
                        ].map((item, i) => (
                            <Grid item xs={12} sm={6} key={i}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                                    <CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 19, mt: 0.1 }} />
                                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>{item}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* FOOTER ACTIONS */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Button
                        component={Link}
                        to="/devops-hub"
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            borderColor: BRAND.gold,
                            color: BRAND.gold,
                            borderRadius: "99px",
                            px: 4,
                            fontWeight: 800,
                            "&:hover": { borderColor: BRAND.goldBright, background: BRAND.goldGlow }
                        }}
                    >
                        Return to DevOps Hub
                    </Button>
                </Box>

                <Box sx={{ mt: 2, p: 2.5, border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.goldGlow, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <LightbulbIcon sx={{ color: BRAND.gold, flexShrink: 0 }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>
                        <strong style={{ color: BRAND.white }}>Pro Tip:</strong> Pushed a secret by mistake? Simply deleting the file in a new commit isn't enough—it stays in your history! You'll need a tool like <strong>BFG Repo-Cleaner</strong> to completely scrub it from all snapshots.
                    </Typography>
                </Box>
            </Container>

            {/* ✅ Floating Scroll-to-Top */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: BRAND.gold, "&:hover": { bgcolor: BRAND.goldBright } }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </Box>
    );
}
